/**
 * @file FiltroDeData.jsx
 * @description Componente reutilizável para filtro de período de datas
 * Este componente renderiza campos de filtro alinhados na mesma linha com responsividade
 */

import { useState } from "react";
import { TextField, Box, Button, Grid, useMediaQuery, useTheme, MenuItem } from "@mui/material";
import DashboardFilters from './DashboardFilters';

// Limita intervalo máximo de 1 ano entre datas
const addYearsToDateString = (dateString, years) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  date.setFullYear(date.getFullYear() + years);
  return date.toISOString().split('T')[0];
};


/**
 * Componente FiltroDeData
 * 
 * @component
 * @param {Object} props - Propriedades do componente
 * @param {string} props.startDate - Data de início selecionada
 * @param {Function} props.setStartDate - Função para atualizar a data de início
 * @param {string} props.endDate - Data de fim selecionada
 * @param {Function} props.setEndDate - Função para atualizar a data de fim
 * @returns {JSX.Element} Componente com filtros alinhados e responsivos
 */

const FiltroDeData = ({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  setDateFilter,
  dateFilter,
  handleApplyFilters,  // Nova prop
  onYearChange         // NOVA prop
}) => {

  const [selectedYear, setSelectedYear] = useState('');

  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = 2023; year <= currentYear; year++) {
    years.push(year);
  }

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setDateFilter('Mês atual'); // ou setDateFilter(null) se preferir
  };

  const handleDateFilterChange = (value) => {
    setDateFilter(value);

    if (value === 'Anual') {
      // Ao selecionar "Anos", limpar datas e ano selecionado
      setStartDate('');
      setEndDate('');
      setSelectedYear('');
    } else if (value !== 'Datas Específicas') {
      // Outros filtros automáticos: limpa datas e ano
      setStartDate('');
      setEndDate('');
      setSelectedYear('');
    } else {
      // Quando "Datas Específicas" é selecionado, definir data de início como primeiro dia do mês
      const hoje = new Date();
      const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      const dataFormatada = primeiroDiaMes.toISOString().split('T')[0];
      setStartDate(dataFormatada);
    }
  };


  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        mt: 2,
        mb: 2,
        display: 'flex',
        flexDirection: 'row',   // garante que seja uma linha
        alignItems: 'center',   // alinha verticalmente
        gap: 2                  // espaçamento entre os componentes (opcional)
      }}
    >
      <Grid
        container
        spacing={2}
        direction={isMobile ? 'column' : 'row'}     // garante que seja uma linha
        alignItems="center"  // alinha verticalmente
      >
        {/* Campo de Filtro por Período */}
        <Grid item xs={12} sm={6} md={3}>
          <DashboardFilters
            dateFilter={dateFilter}
            onChange={handleDateFilterChange}
            marginB={0}
          />
        </Grid>

        {dateFilter === 'Anual' && (
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label="Ano"
              value={selectedYear}
              onChange={(e) => {
                const year = e.target.value;
                setSelectedYear(year);
                if (onYearChange) {
                  onYearChange(year); // dispara atualização global e requisição
                }
              }}
              variant="outlined"
              size="small"
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 1,
                width: 145,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#667eea',
                  opacity: 1,
                },
              }}
            >
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}


        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: { xs: 2, md: 3 },
            p: { xs: 1, sm: 1.5, md: 1.5 },
            pb: { xs: 3, sm: 1.5, md: 1.5 },
            boxShadow: {
              xs: '0 2px 10px rgba(0,0,0,0.08)',
              md: '0 4px 20px rgba(0,0,0,0.08)'
            },
            border: '1px solid rgba(0,0,0,0.05)',
            display: dateFilter !== 'Datas Específicas' ? 'none' : 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
          }}
        >

          {/* Campo Data de Início */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              type="date"
              label="Data de Início"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={dateFilter !== 'Datas Específicas'} // Adicione esta linha
              variant="outlined"
              size="small"
              error={startDate && endDate && startDate > endDate}
              helperText={startDate && endDate && startDate > endDate ? "Data de início não pode ser posterior à data de fim" : ""}
              inputProps={{
                max: endDate || undefined,
                ...(endDate && { min: addYearsToDateString(endDate, -1) }) // não deixa passar de 1 ano
              }}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 1,
                width: 145,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#667eea',
                  opacity: 1,
                },
              }}
            />
          </Grid>

          {/* Campo Data de Fim */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              type="date"
              label="Data de Fim"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={dateFilter !== 'Datas Específicas'} // Adicione esta linha
              variant="outlined"
              size="small"
              error={startDate && endDate && endDate < startDate}
              helperText={startDate && endDate && endDate < startDate ? "Data de fim não pode ser anterior à data de início" : ""}
              inputProps={{
                min: startDate || undefined,
                ...(startDate && { max: addYearsToDateString(startDate, 1) }) // máximo 1 ano após o início
              }}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 1,
                width: 145,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#667eea',
                  opacity: 1,
                },
              }}
            />
          </Grid>

          {/* Botão Aplicar Filtros */}
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              disabled={dateFilter !== 'Datas Específicas'}
              sx={{
                height: '40px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontWeight: 600,
                borderRadius: 1.5,
                textTransform: 'none',
                fontSize: '0.875rem',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                width: 145,
                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.2)',
                '&:hover:not(.Mui-disabled)': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.3)',
                  transform: 'translateY(-1px)',
                },
                '&.Mui-disabled': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  background: 'linear-gradient(135deg, #9c9999 0%, #8a8a8a 100%)',
                  boxShadow: '0 1px 3px rgba(156, 153, 153, 0.2)',
                  transform: 'translateY(0)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'not-allowed',
                  // Remove qualquer transformação ou sombra do hover
                  '&:hover': {
                    background: 'linear-gradient(135deg, #9c9999 0%, #8a8a8a 100%)',
                    boxShadow: '0 1px 3px rgba(156, 153, 153, 0.2)',
                    transform: 'translateY(0)',
                  }
                },
              }}
            >
              Aplicar Filtros
            </Button>
          </Grid>

        </Box>



        {/* Botão Limpar Filtros */}
        <Grid item xs={12} sm={6} md={3}>
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            sx={{
              height: '40px', // Altura igual aos TextFields small
              borderColor: 'primary.main',
              color: 'primary.main',
              bgcolor: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(42, 133, 226, 0.12)'
                  : 'transparent',
              fontWeight: 600,
              borderRadius: 1.5,
              textTransform: 'none',
              fontSize: '0.875rem',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              width: 145,
              '&:hover': {
                borderColor: 'primary.light',
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(42, 133, 226, 0.22)'
                    : 'rgba(102, 126, 234, 0.08)',
                boxShadow: (theme) =>
                  theme.palette.mode === 'dark'
                    ? '0 4px 14px rgba(42, 133, 226, 0.18)'
                    : '0 4px 14px rgba(102, 126, 234, 0.16)',
                transform: 'translateY(-1px)',
              },
              '&:active': {
                transform: 'translateY(0px)',
              },
            }}
          >
            Limpar Filtros
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FiltroDeData;
